'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef, useEffect } from 'react';
import { styled } from '@classify/react-core-components/styles';
import Typography from '@classify/react-core-components/Typography';
import { translate, getActiveLang, getActiveCountry } from '@openclassrooms/translations';
import QuizLearningStatus from '../../../[chapterSlug]/_components/LearningStatus/QuizLearningStatus';
type QuizCompletedStatus = 'retry' | 'failed' | 'passed';
interface Props {
  quizLearningStatus: QuizCompletedStatus;
  quizReinitializationDate?: string;
  hasResetQuiz?: boolean;
}
const BannerRoot = styled('div')(({
  theme: {
    spacing
  }
}) => ({
  display: 'flex',
  flexDirection: 'row',
  padding: spacing(1)
}));
const IconContainer = styled('div')(({
  theme: {
    spacing,
    breakpoints
  }
}) => ({
  display: 'none',
  [breakpoints.up('sm')]: {
    display: 'block',
    padding: spacing(2, 0, 2, 1)
  }
}));
const ContentBlock = styled('div')(({
  theme: {
    spacing,
    palette
  }
}) => ({
  minWidth: 0,
  padding: spacing(2),
  color: palette.text.onMain,
  '& a': {
    color: 'inherit'
  }
}));
const STATUS_BACKGROUNDS: Record<string, string> = {
  failed: 'notification.warning.background',
  retry: 'notification.info.background',
  passed: 'notification.valid.background'
};
const QuizCompletionBanner = t0 => {
  const $ = _c(32);
  const {
    quizLearningStatus,
    quizReinitializationDate,
    hasResetQuiz
  } = t0;
  const bannerRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getActiveLang();
    t2 = getActiveCountry();
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  const locale = `${t1}-${t2}`;
  let t3;
  let t4;
  let t5;
  if ($[2] !== quizReinitializationDate) {
    const formatter = new Intl.DateTimeFormat(locale, {
      dateStyle: "long",
      timeStyle: "short"
    });
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        title: translate("course.chapter.quiz.to_retake.ready.status"),
        description: translate("course.chapter.quiz.to_retake.ready.desc")
      };
      t3 = translate("course.chapter.quiz.to_retake.wait.status");
      $[6] = t3;
      $[7] = t5;
    } else {
      t3 = $[6];
      t5 = $[7];
    }
    t4 = quizReinitializationDate ? translate("course.chapter.quiz.to_retake_date.wait.desc", {
      date: formatter.format(new Date(quizReinitializationDate))
    }) : "";
    $[2] = quizReinitializationDate;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t4) {
    t6 = {
      title: t3,
      description: t4
    };
    $[8] = t3;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      title: translate("course.chapter.quiz.successfully_completed.status"),
      description: ""
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t5 || $[13] !== t6) {
    t8 = {
      retry: t5,
      failed: t6,
      passed: t7
    };
    $[12] = t5;
    $[13] = t6;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const quizBannerConfig = t8;
  let t10;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      if (bannerRef.current) {
        bannerRef.current?.focus();
      }
    };
    t10 = [];
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  useEffect(t9, t10);
  const learningStatusToUse = hasResetQuiz ? "retry" : quizLearningStatus;
  const banner = quizBannerConfig[learningStatusToUse];
  if (!banner) {
    return null;
  }
  const {
    title,
    description
  } = banner;
  const t11 = STATUS_BACKGROUNDS[learningStatusToUse];
  let t12;
  if ($[17] !== t11) {
    t12 = {
      backgroundColor: t11,
      mb: 2
    };
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const t13 = learningStatusToUse as any;
  let t14;
  if ($[19] !== t13) {
    t14 = <IconContainer><QuizLearningStatus status={t13} ariaHidden={true} /></IconContainer>;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== title) {
    t15 = <Typography variant="subheading">{title}</Typography>;
    $[21] = title;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== description) {
    t16 = description && <Typography sx={{
      marginTop: 1
    }}>{description}</Typography>;
    $[23] = description;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t15 || $[26] !== t16) {
    t17 = <ContentBlock>{t15}{t16}</ContentBlock>;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t12 || $[29] !== t14 || $[30] !== t17) {
    t18 = <BannerRoot sx={t12} tabIndex={-1} ref={bannerRef}>{t14}{t17}</BannerRoot>;
    $[28] = t12;
    $[29] = t14;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  return t18;
};
export default QuizCompletionBanner;